import { css } from '@emotion/react';
import {
  faCircleInfo, faPencil, faScaleBalanced, faSync, faTrash, faValueAbsolute,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type ForwardedRef, forwardRef,
  useCallback,
  useMemo,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  LottieAnimationComponent, LottieAnimationTypes,
} from '~/_shared/baseComponents/lottieAnimation';
import {
  ToggleComponent, ToggleSize,
} from '~/_shared/baseComponents/toggle';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import {
  PermanentConfirmStrategy, useConfirmationModal,
} from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { OverlayLottieAnimationComponent } from '~/_shared/components/overlay/overlayLottieAnimation.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ChartItem } from './chartItem.types';
import { ChartItemVisualizer } from './chartItemVisualizer.component';

export const CHART_ITEM_HEIGHT = 300;

const containerStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.secondary,
  border: `1px solid ${theme.borderColors.activeItem}`,
  borderRadius: 4,
  height: CHART_ITEM_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const headerStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.highlight,
  color: theme.textColors.primary,
  height: 38,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 7px',
  gap: 20,
});

const titleStyle = css({
  fontSize: 20,
  fontWeight: 500,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
});

const bottomPartStyle = css({
  padding: '0 10px 10px 10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
});

const statusBarStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  fontSize: 8,
  height: 13,
  alignItems: 'center',
  gap: 4,
  cursor: 'default',
});

const statusBarItemStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  lineHeight: '13px',
  height: 13,
  alignItems: 'center',
  gap: 2,
});

const righSideHeaderActionsContainerStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  lineHeight: '14px',
  height: 14,
  fontSize: 14,
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: 11,
});

const statusBarNoteItemStyle = (theme: Theme) => css({
  color: theme.textColors.info,
});

const statusBarDangerItemStyle = (theme: Theme) => css({
  color: theme.textColors.warning,
});

const actionIconStyle = ({ isDisabled, isDanger }: { isDisabled?: boolean; isDanger?: boolean }) => (theme: Theme) => css({
  color: isDisabled ? theme.textColors.disabled : theme.textColors.primary,
  cursor: isDisabled ? 'not-allowed' : 'default',
  fontSize: 14,
  height: 14,
  width: 14,
  '&:hover': {
    color: isDisabled ? theme.textColors.disabled :
      (isDanger ? theme.textColors.warning : theme.textColors.linkOnHover),
  },
});

export const ChartItemComponent = forwardRef((props: ChartItem, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    title,
    isSyncing,
    isLoading,
    isOutdated,
    shouldNormalize,
    metrics,
    onDeleteClick,
    onEditClick,
    onNormalizeToggleClick,
    onRefreshClick,
  } = props;

  const [t] = useTranslation();
  const theme = useTheme();
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();

  const syncingChartColors = useMemo(() => ({
    bar1: theme.textColors.info,
    bar2: theme.textColors.info,
    bar3: theme.textColors.info,
    bar4: theme.textColors.info,
  }), [theme]);

  const handleDeleteClick = useCallback(() => {
    openConfirmationModal({
      title: t('Delete Chart'),
      text: t('Are you sure you want to delete this chart?'),
      onConfirm: () => {
        closeConfirmationModal();
        onDeleteClick?.();
      },
      onCancel: closeConfirmationModal,
      isConfirmButtonDestructive: true,
      confirmCaption: t('Proceed'),
      permanentConfirmSettings: {
        id: 'delete-split-screen-chart',
        strategy: PermanentConfirmStrategy.Session,
      },
    });
  }, [closeConfirmationModal, onDeleteClick, openConfirmationModal, t]);

  const isForceAbsoluteValue = metrics.barValues.length === 1;

  return (
    <div ref={ref} css={containerStyle}>
      <div css={headerStyle}>
        <div css={titleStyle}>
          {title}
        </div>
        <div css={righSideHeaderActionsContainerStyle}>
          {onNormalizeToggleClick && !isForceAbsoluteValue && (
            <TooltipComponent tooltipContent={t('charts.normalize.shortDescription')}>
              <span>
                <ToggleComponent
                  offIcon={faValueAbsolute}
                  offIconTopOffset={1}
                  onIcon={faScaleBalanced}
                  onIconTopOffset={1}
                  size={ToggleSize.M}
                  isOn={!!shouldNormalize}
                  onChange={(newValue: boolean) => onNormalizeToggleClick?.(newValue)}
                />
              </span>
            </TooltipComponent>
          )}
          <TooltipComponent tooltipContent={onRefreshClick || isSyncing ? undefined : t('charts.refresh.upToDate')}>
            <FontAwesomeIcon
              icon={faSync}
              spin={isSyncing}
              css={actionIconStyle({ isDisabled: !onRefreshClick || isSyncing })}
              onClick={onRefreshClick}
              title={t('charts.refresh.upToDate')}
            />
          </TooltipComponent>
          <FontAwesomeIcon
            icon={faPencil}
            css={actionIconStyle({ isDisabled: !onEditClick })}
            onClick={onEditClick}
            title={t('Edit')}
          />
          {onDeleteClick && (
            <FontAwesomeIcon
              icon={faTrash}
              css={actionIconStyle({ isDanger: true })}
              onClick={handleDeleteClick}
              title={t('Delete')}
            />
          )}
        </div>
      </div>
      <div css={bottomPartStyle}>
        {isLoading && (
          <OverlayLottieAnimationComponent
            type={LottieAnimationTypes.BarGraph}
            size={120}
            autoplay
            loop
          />
        )}
        <div css={statusBarStyle}>
          {isOutdated && (
            <TooltipComponent
              tooltipContent={t('charts.outdated.tooltip')}
              behavior={TooltipBehavior.ShowOnHover}
            >
              <span css={[statusBarItemStyle, statusBarDangerItemStyle]}>
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>{t('charts.outdated.label')}</span>
              </span>
            </TooltipComponent>
          )}
          {isSyncing && (
            <span css={[statusBarItemStyle, statusBarNoteItemStyle]}>
              <span>{t('Syncing')}</span>
              <span>
                <LottieAnimationComponent
                  type={LottieAnimationTypes.BarGraph}
                  size={7}
                  colors={syncingChartColors}
                  autoplay
                  loop
                />
              </span>
            </span>
          )}
        </div>
        <div>
          <ChartItemVisualizer
            {...props}
            shouldNormalize={isForceAbsoluteValue ? false : shouldNormalize}
          />
        </div>
      </div>
    </div>
  );
});
